import React, { useState } from 'react';
import { SideNavbar, Modal, Button } from '@sunrun/component-library';
import {
  BarChartCircle,
  HouseCircle,
  MoneySignCircle,
  MultiplePeopleCircle,
  PersonCircle,
  PowerButtonCircle,
  QuestionmarkCircle,
} from '@sunrun/component-library/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import withSizes from 'react-sizes';
import {
  AUTOPAY_ENROLL_ROUTE,
  BILLING_HISTORY_ROUTE,
  BILLING_ROUTE,
  DASHBOARD_HISTORY_ROUTE,
  DASHBOARD_ROUTE,
  DOCUMENT_LIST_ROUTE,
  FAQ_ROUTE,
  LOGIN_ROUTE,
  PAYMENT_OPTIONS_ROUTE,
  PROFILE_ROUTE,
  PROFILE_DETAILS_ROUTE,
  REFERRALS_ROUTE,
  SUPPORT_ROUTE,
  SOLAR_GUIDE_ROUTE,
} from '../../constants/routes';
import { useBillingEnabled, useOpportunity, usePrePTOStatus, useIsEVChargerProspect } from '../../hooks/useUser';
import { useHideBillingFlag } from '../../hooks/flagsmith/useHideBillingFlag';
import mixpanel from '../../utilities/mixpanel';
import mapSizesToProps from '../general/utilities/mapSizesToProps';
import AddressSelectorContainer from './AddressSelector.container';

const Navigation = ({
  ACHEligible,
  drawerToggle,
  hasAutoPay,
  isCurrentOwner,
  isMobile,
  isOpen,
  isRelatedContact,
  loading,
  monthlyType,
  onLogout,
  referralEligible,
  rootId,
  testId,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const oppty = useOpportunity();
  const prePTOCustomer = usePrePTOStatus();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const billingEnabled = useBillingEnabled();
  const hasContract = oppty && !!oppty.contract;
  const isEVChargerProspect = useIsEVChargerProspect();
  const hideBilling = useHideBillingFlag();

  return (
    <div data-testid={testId}>
      <SideNavbar.Container fixed isOpen={isOpen} onClickClose={drawerToggle}>
        <AddressSelectorContainer drawerToggle={drawerToggle} />
        <SideNavbar.MenuGroup>
          {!loading && getMenuItems()}
          <SideNavbar.MenuItem
            text={t('Log Out')}
            icon={PowerButtonCircle}
            onClick={() => {
              mixpanel.track('Initiated Logout', { Location: pathname });
              setModalOpen(true);
            }}
            isBottomAligned
          />
        </SideNavbar.MenuGroup>
      </SideNavbar.Container>
      <Modal.Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} rootId={rootId} ariaHideApp={!!rootId} hasCloseButton={false}>
        <Modal.Body>Are you sure you want to log out of mySunrun?</Modal.Body>
        <Modal.Buttons>
          <Button data-testid="logout-button" primary onClick={onLogout} href={LOGIN_ROUTE}>
            Log Out
          </Button>
          <Button
            secondary
            onClick={() => {
              mixpanel.track('Canceled Logout', { Location: pathname });
              setModalOpen(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Buttons>
      </Modal.Modal>
    </div>
  );

  function getMenuItems() {
    return (
      <>
        {isCurrentOwner && (
          <SideNavbar.MenuItem
            text={t('Home')}
            icon={HouseCircle}
            active={pathname === DASHBOARD_ROUTE}
            onClick={() => {
              mixpanel.track('Visited Home');
              drawerToggle();
            }}
            to={DASHBOARD_ROUTE}
          />
        )}
        {!prePTOCustomer && hasContract && isCurrentOwner && !isEVChargerProspect && (
          <SideNavbar.MenuItem
            text={t('Energy System')}
            icon={BarChartCircle}
            active={pathname.includes(DASHBOARD_HISTORY_ROUTE)}
            to={DASHBOARD_HISTORY_ROUTE}
            onClick={drawerToggle}
            data-tour="energy-nav"
          />
        )}
        {!hideBilling && (
          <SideNavbar.MenuItem
            text={t('Billing')}
            icon={MoneySignCircle}
            isOpen={pathname.includes(BILLING_ROUTE)}
            active={
              pathname.includes(BILLING_ROUTE) &&
              !pathname.includes(`${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}`) &&
              !pathname.includes(`${BILLING_ROUTE}${BILLING_HISTORY_ROUTE}`)
            }
            to={BILLING_ROUTE}
            onClick={drawerToggle}
          >
            <SideNavbar.Submenu>
              {monthlyType && isCurrentOwner && billingEnabled && ACHEligible && (
                <SideNavbar.SubmenuItem
                  text={t('Manage Autopay')}
                  active={pathname.includes(`${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}`)}
                  to={!hasAutoPay ? `${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}/${AUTOPAY_ENROLL_ROUTE}` : `${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}`}
                  onClick={drawerToggle}
                />
              )}
              {!isRelatedContact && (
                <SideNavbar.SubmenuItem
                  text={t('Billing History')}
                  active={pathname.includes(`${BILLING_ROUTE}${BILLING_HISTORY_ROUTE}`)}
                  to={`${BILLING_ROUTE}${BILLING_HISTORY_ROUTE}`}
                  onClick={drawerToggle}
                />
              )}
            </SideNavbar.Submenu>
          </SideNavbar.MenuItem>
        )}
        <SideNavbar.MenuItem
          text={t('Account')}
          icon={PersonCircle}
          isOpen={pathname.includes(PROFILE_ROUTE) || pathname.includes(DOCUMENT_LIST_ROUTE)}
          active={pathname.includes(PROFILE_ROUTE)}
          to={isMobile ? PROFILE_ROUTE : `${PROFILE_ROUTE}/${PROFILE_DETAILS_ROUTE}`}
          onClick={drawerToggle}
        >
          <SideNavbar.Submenu>
            <SideNavbar.SubmenuItem
              text={t('Documents')}
              active={pathname.includes(DOCUMENT_LIST_ROUTE)}
              to={{
                pathname: DOCUMENT_LIST_ROUTE,
                state: {
                  navigatedFrom: 'Main Nav',
                },
              }}
              onClick={drawerToggle}
            />
          </SideNavbar.Submenu>
        </SideNavbar.MenuItem>
        {referralEligible && !isEVChargerProspect && (
          <SideNavbar.MenuItem
            text={t('Refer a Friend')}
            icon={MultiplePeopleCircle}
            active={pathname.includes(REFERRALS_ROUTE)}
            to={{
              pathname: REFERRALS_ROUTE,
              state: {
                navigatedFrom: 'Primary Nav',
              },
            }}
            onClick={drawerToggle}
          />
        )}
        <SideNavbar.MenuItem
          text={t('Support')}
          icon={QuestionmarkCircle}
          isOpen={pathname.includes(SUPPORT_ROUTE) || pathname.includes(FAQ_ROUTE) || pathname.includes(SOLAR_GUIDE_ROUTE)}
          active={pathname.includes(SUPPORT_ROUTE)}
          to={SUPPORT_ROUTE}
          onClick={drawerToggle}
        >
          {!isEVChargerProspect && (
            <SideNavbar.Submenu>
              <SideNavbar.SubmenuItem
                text={t('FAQs')}
                active={pathname.includes(FAQ_ROUTE)}
                to={{
                  pathname: FAQ_ROUTE,
                  state: { navigatedFrom: 'Main Nav' },
                }}
                onClick={drawerToggle}
              />
              <SideNavbar.SubmenuItem
                text={t('Solar Guide')}
                active={pathname.includes(SOLAR_GUIDE_ROUTE)}
                to={{
                  pathname: SOLAR_GUIDE_ROUTE,
                  state: { navigatedFrom: 'Main Nav' },
                }}
                onClick={drawerToggle}
              />
            </SideNavbar.Submenu>
          )}
        </SideNavbar.MenuItem>
      </>
    );
  }
};

Navigation.propTypes = {
  ACHEligible: PropTypes.bool,
  drawerToggle: PropTypes.func.isRequired,
  hasAutoPay: PropTypes.bool,
  isCurrentOwner: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isRelatedContact: PropTypes.bool,
  loading: PropTypes.bool,
  monthlyType: PropTypes.bool,
  onLogout: PropTypes.func.isRequired,
  referralEligible: PropTypes.bool.isRequired,
  rootId: PropTypes.string,
  testId: PropTypes.string.isRequired,
};

Navigation.defaultProps = {
  ACHEligible: false,
  hasAutoPay: false,
  isCurrentOwner: true,
  isRelatedContact: false,
  loading: true,
  monthlyType: true,
  rootId: null,
};

export default withSizes(mapSizesToProps)(Navigation);
